/**
 * @module
 * generatePerfCheckpoints
 */

import convertTimesArrayToString from './convertTimesArrayToString';
import { CardLoadPerfCheckpoints } from 'owa-actionable-message-v2';
import { differenceInMilliseconds } from 'owa-datetime';

/**
 * @function
 * To check whether an object is empty or not
 * @param {any} obj - An object which needs to checked for empty
 * @returns The object is empty or not
 */

function isEmptyObject(obj): boolean {
    return Object.keys(obj).length === 0;
}

/**
 * @function
 * To generate the generic info out of the performance check points of the card
 * @param {CardLoadPerfCheckpoints} perfCheckpoints - The data check points of the card performance
 * @returns The string having the generic info of all the performance timings
 */

export default function generatePerfCheckpoints(perfCheckpoints: CardLoadPerfCheckpoints): string {
    /**
     * In case if the card has an auto-invoke action which updates the new refresh card,
     * then the initial card will be the one which is loaded first and the final card will
     * be the refresh card
     *
     * In case if the card does not have an auto-invoke action then the card which is loaded
     * will be the initial card and there won't be final card
     */

    const renderingTimes = [];

    const initialCardCheckpoints =
        perfCheckpoints.previousCardCheckpoints &&
        !isEmptyObject(perfCheckpoints.previousCardCheckpoints)
            ? perfCheckpoints.previousCardCheckpoints
            : perfCheckpoints.currentCardCheckpoints;

    let finalCardCheckpoints: any = null;
    if (
        perfCheckpoints.previousCardCheckpoints &&
        !isEmptyObject(perfCheckpoints.previousCardCheckpoints)
    ) {
        finalCardCheckpoints = perfCheckpoints.currentCardCheckpoints;
    }

    /**
     * Calculate time spent to process the card json to create initial card instance if present
     */
    const initialCardProcessingTime = differenceInMilliseconds(
        initialCardCheckpoints.cardCreateEnd,
        initialCardCheckpoints.cardCreateStart
    );

    renderingTimes['ICPT'] = initialCardProcessingTime;

    /**
     * Calculate time spent to create the HTML DOM element out of the initial card instance if present
     */
    const initialCardRenderingTime = differenceInMilliseconds(
        initialCardCheckpoints.cardRenderEnd,
        initialCardCheckpoints.cardRenderStart
    );

    renderingTimes['ICRT'] = initialCardRenderingTime;

    /**
     * Calculate the time spent waiting for the theme to load for initial card if present
     */
    const initialCardThemeLoadTime = differenceInMilliseconds(
        initialCardCheckpoints.themeLoadTotalEnd,
        initialCardCheckpoints.themeLoadTotalStart
    );

    renderingTimes['ICTT'] = initialCardThemeLoadTime;

    /**
     * Calculate the time spent fetching the initial card (i.e. time spent waiting for getItem to get the cards)
     */
    const initialCardTimeSpendFetching = initialCardCheckpoints.totalFetchTime;

    renderingTimes['ICFT'] = initialCardTimeSpendFetching;

    /**
     * Calculate the total time spent in the adaptive card js library's render function for initial card if present
     */
    const initialCardHtmlRenderingTime = differenceInMilliseconds(
        initialCardCheckpoints.cardLibraryRenderEnd,
        initialCardCheckpoints.cardLibraryRenderStart
    );

    renderingTimes['ICHT'] = initialCardHtmlRenderingTime;

    /**
     * Calculate the total time spent in the post-render preparation for initial card if present (i.e. fixing image references, adjusting spacing, etc.)
     */
    const initialCardPostRenderPrepareCardTime = differenceInMilliseconds(
        initialCardCheckpoints.prepareCardEnd,
        initialCardCheckpoints.prepareCardStart
    );

    renderingTimes['IPPT'] = initialCardPostRenderPrepareCardTime;

    if (finalCardCheckpoints) {
        /**
         * Calculate time spent to process the card json to create final card instance
         */
        const finalCardProcessingTime = differenceInMilliseconds(
            finalCardCheckpoints.cardCreateEnd,
            finalCardCheckpoints.cardCreateStart
        );

        renderingTimes['FCPT'] = finalCardProcessingTime;

        /**
         * Calculate time spent to create the HTML DOM element out of the final card instance
         */
        const finalCardRenderingTime = differenceInMilliseconds(
            finalCardCheckpoints.cardRenderEnd,
            finalCardCheckpoints.cardRenderStart
        );

        renderingTimes['FCRT'] = finalCardRenderingTime;

        /**
         * Calculate the time spent waiting for the theme to load for final card
         */
        const finalCardThemeLoadTime = differenceInMilliseconds(
            finalCardCheckpoints.themeLoadTotalEnd,
            finalCardCheckpoints.themeLoadTotalStart
        );

        renderingTimes['FCTT'] = finalCardThemeLoadTime;

        if (finalCardCheckpoints.totalFetchTime) {
            /**
             * Calculate the time spent fetching the final card (i.e. time spent waiting for getItem to get the cards)
             */
            const finalCardTimeSpendFetching = finalCardCheckpoints.totalFetchTime;

            renderingTimes['FCFT'] = finalCardTimeSpendFetching;
        }

        /**
         * Calculate the total time spent in the adaptive card js library's render function for final card
         */
        const finalCardHtmlRenderingTime = differenceInMilliseconds(
            finalCardCheckpoints.cardLibraryRenderEnd,
            finalCardCheckpoints.cardLibraryRenderStart
        );

        renderingTimes['FCHT'] = finalCardHtmlRenderingTime;

        /**
         * Calculate the total time spent in the post-render preparation for final card (i.e. fixing image references, adjusting spacing, etc.)
         */
        const finalCardPostRenderPrepareCardTime = differenceInMilliseconds(
            finalCardCheckpoints.prepareCardEnd,
            finalCardCheckpoints.prepareCardStart
        );

        renderingTimes['FPPT'] = finalCardPostRenderPrepareCardTime;
    }

    if (perfCheckpoints.autoInvokeActionStart && perfCheckpoints.autoInvokeActionEnd) {
        /**
         * Calculate the time spent waiting for auto-invoke actions to complete
         */
        const autoInvokeTime = differenceInMilliseconds(
            perfCheckpoints.autoInvokeActionEnd,
            perfCheckpoints.autoInvokeActionStart
        );

        renderingTimes['AIT'] = autoInvokeTime;
    }

    /**
     * Calculate the total time spent getting the final card ready to render
     * This measures the total time from when the item is first loaded in the reading pane until
     * the final card is rendered (after any auto-exec actions are run)
     */
    const totalCardRenderingTime = differenceInMilliseconds(
        finalCardCheckpoints
            ? finalCardCheckpoints.cardRenderEnd
            : initialCardCheckpoints.cardRenderEnd,
        perfCheckpoints.loadStart
    );

    renderingTimes['TCRT'] = totalCardRenderingTime;

    return convertTimesArrayToString(renderingTimes);
}
